import $ from 'jquery';
import { createContext } from '../context';
import { eventEmitter } from '../global/eventEmitter';
import {
    debounce,
    buildCustomEvent,
} from '../util/dom';
import { EV_BREAKPOINT_CHANGE } from '../constants/events';
import { BREAKPOINTS } from '../constants/breakpoints';

/**
 * Base Controller
 *
 * @class BaseController
 */
export class BaseController {
    constructor() {
        this.eventEmitter = eventEmitter;
        /**
         * A reference to the window
         *
         * @property $window
         * @type {jQuery}
         * @public
         */
        this.$window = null;

        /**
         * currentSize
         *
         * @property currentSize
         * @type {string}
         * @public
         */
        this.currentSize = null;

        /**
         * previousSize
         *
         * @property previousSize
         * @type {string}
         * @public
         */
        this.previousSize = null;

        /**
         * Duration of debounce to use for firing resize events in the app
         *
         * @property resizeDebounceDuration
         * @type {int}
         */
        this.resizeDebounceDuration = 250;

        /**
         * A reference to the body
         *
         * @property $body
         * @type {jQuery}
         * @public
         */
        this.$body = null;

        /**
         * Is the base enabled
         *
         * @property isBaseEnabled
         * @type {Boolean}
         * @default false
         * @public
         */
        this.isBaseEnabled = false;

        this.baseInit();
    }

    /**
     * Initializes the UI Component View
     * Runs setupHandlers, createChildren, and enable
     *
     * @method baseInit
     * @public
     * @chainable
     */
    baseInit() {
        this.$window = $(window);
        this.$body = $('body');

        this.baseEnable()
            .getCurrentSize();

        return this;
    }

    /**
     * Enables the base controller
     *
     * @method baseEnable
     * @returns {BaseController}
     */
    baseEnable() {
        if (this.isBaseEnabled) {
            return this;
        }

        this.$window.on('resize orientationchange', debounce(this.onResize.bind(this), this.resizeDebounceDuration));

        this.isBaseEnabled = false;

        return this;
    }

    /**
     * Disables the base controller
     *
     * @method baseDisable
     * @returns {BaseController}
     */
    baseDisable() {
        if (!this.isBaseEnabled) {
            return this;
        }

        this.$window.off('resize orientationchange', debounce(this.onResize.bind(this), this.resizeDebounceDuration));

        this.isBaseEnabled = false;

        return this;
    }

    /**
     * Get Current Size
     *
     * @method getCurrentSize
     * @public
     * @chainable
     */
    getCurrentSize() {
        return this.currentSize = window.getComputedStyle(document.body, ':after').getPropertyValue('content').replace(/"/g, '');
    }

    /**
     * Checking current size for
     * breakpoint change
     *
     * @method checkSize
     * @public
     * @chainable
     */
    checkSize(currentSize) {
        this.getCurrentSize();
        if (this.currentSize !== currentSize) {
            this.previousSize = currentSize;
            this.currentSize = this.currentSize;

            const bp = { previousSize: this.previousSize, currentSize: this.currentSize };
            this.onBreakpointChange(bp);
        }

        return this;
    }

    /**
     * Destroy the controller
     *
     * @method destroy
     * @returns {BaseController}
     */
    destroy() {
        let key;

        this.baseDisable()
            .onDestroy();

        for (key in this) {
            if (this.hasOwnProperty(key)) {
                this[key] = null;
            }
        }
    }

    /**
     * Returns boolean value of whether
     * breakpoint is mobile or not.
     *
     * @method isMobile
     * @public
     * @chainable
     */
    isMobile() {
        /* Perform necessary actions depending on which breakpoint we're on*/
        if (this.currentSize === BREAKPOINTS.SCREEN ||
            this.currentSize === BREAKPOINTS.SMALL) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Returns boolean value of whether
     * breakpoint is desktop or not.
     *
     * @method isDesktop
     * @public
     * @chainable
     */
    isDesktop() {
        /* Perform necessary actions depending on which breakpoint we're on*/
        if (this.currentSize === BREAKPOINTS.LARGE ||
            this.currentSize === BREAKPOINTS.XLARGE) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * onResize
     *
     * @method onResize
     * @public
     * @chainable
     */
    onResize() {
        this.checkSize(this.currentSize);
        this.onWindowResize();

        return this;
    }

    onDestroy() {
        // noop
    }

    onUpdated() {
        // noop
    }

    onWindowResize() {
        // noop
    }

    onBreakpointChange() {
        // noop
    }

}